import { jsPDF } from 'jspdf';

// ─── Fix de acentos ──────────────────────────────────────────────────────────
function safe(text) {
  if (!text) return '';
  return String(text)
    .replace(/[àáâãä]/g, 'a').replace(/[ÀÁÂÃÄ]/g, 'A')
    .replace(/[èéêë]/g,  'e').replace(/[ÈÉÊË]/g,  'E')
    .replace(/[ìíîï]/g,  'i').replace(/[ÌÍÎÏ]/g,  'I')
    .replace(/[òóôõö]/g, 'o').replace(/[ÒÓÔÕÖ]/g, 'O')
    .replace(/[ùúûü]/g,  'u').replace(/[ÙÚÛÜ]/g,  'U')
    .replace(/[ç]/g,     'c').replace(/[Ç]/g,     'C')
    .replace(/[ñ]/g,     'n').replace(/[Ñ]/g,     'N')
    .replace(/[–—]/g,    '-')
    .replace(/['']/g,    "'")
    .replace(/[""]/g,    '"')
    .replace(/[^\x00-\xFF]/g, '');
}

// ─── Paleta de cores ─────────────────────────────────────────────────────────
const COLORS = {
  primary:     [22,  58, 114],   // azul marinho escuro
  primaryDark: [14,  38,  80],   // azul ainda mais escuro (gradiente simulado)
  accent:      [37, 137, 210],   // azul médio
  accentLight: [90, 170, 230],   // azul claro
  light:       [240, 246, 255],  // azul quase branco
  lightAlt:    [248, 251, 255],  // para linhas alternadas
  dark:        [20,  20,  20],   // quase preto
  muted:       [110, 118, 135],  // cinza azulado
  white:       [255, 255, 255],
  border:      [210, 220, 235],
  success:     [39, 174,  96],   // verde (status pago)
  warning:     [230, 160,   0],  // amarelo (pendente)
  danger:      [200,  50,  50],  // vermelho (cancelada)
  gold:        [212, 175,  55],  // dourado (premium badge)
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function setColor(doc, color, target = 'text') {
  if (target === 'text') doc.setTextColor(...color);
  else if (target === 'fill') doc.setFillColor(...color);
  else if (target === 'draw') doc.setDrawColor(...color);
}

function truncate(doc, text, maxWidth) {
  if (doc.getTextWidth(text) <= maxWidth) return text;
  while (text.length > 0 && doc.getTextWidth(text + '...') > maxWidth) {
    text = text.slice(0, -1);
  }
  return text + '...';
}

/** Converte URL do Cloudinary para base64 */
async function urlToBase64(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Cor do badge de status */
function statusColor(status) {
  const map = {
    paga:       COLORS.success,
    confirmada: COLORS.accent,
    enviada:    COLORS.accentLight,
    pendente:   COLORS.warning,
    cancelada:  COLORS.danger,
  };
  return map[(status || '').toLowerCase()] || COLORS.muted;
}

/** Cabeçalho de seção com ícone lateral colorido */
function sectionHeader(doc, title, y, pageW) {
  const margin = 40;
  const h      = 24;

  // Faixa lateral colorida
  setColor(doc, COLORS.accent, 'fill');
  doc.rect(margin, y, 4, h, 'F');

  // Fundo claro
  setColor(doc, COLORS.light, 'fill');
  doc.rect(margin + 4, y, pageW - margin * 2 - 4, h, 'F');

  // Borda inferior sutil
  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.3);
  doc.line(margin, y + h, pageW - margin, y + h);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  setColor(doc, COLORS.primary);
  doc.text(safe(title).toUpperCase(), margin + 12, y + 16);

  return y + h + 14;
}

/** Linha de separação sutil */
function divider(doc, y, pageW, margin = 40) {
  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  return y + 10;
}


// ─── Geração do PDF ──────────────────────────────────────────────────────────
export async function generateInvoicePdf(invoice, user, setUser, setFlashMessage, registerPdf) {
  console.log("User.localization do PDF: ", user.localization);
  console.log("invoice do PDF: ", invoice);
 const result = await registerPdf();

  if (result.limitReached) {
    setFlashMessage(result.message, 'error');
    return { limitReached: true };
  }
  if (result.error) {
    return { error: true };
  }

  const doc    = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const margin = 40;
  const colMid = pageW / 2;
  let y        = 0;

  // ── Carregar logo se existir ─────────────────────────────────────────────
  let logoBase64 = null;
  if (user.image) {
    try {
      logoBase64 = await urlToBase64(user.image);
    } catch (e) {
      console.warn('Nao foi possivel carregar a logo:', e);
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // CABEÇALHO
  // ══════════════════════════════════════════════════════════════════════════

  const headerH = logoBase64 ? 90 : 72;

  // Fundo principal do header
  setColor(doc, COLORS.primary, 'fill');
  doc.rect(0, 0, pageW, headerH, 'F');

  // Faixa decorativa inferior do header
  setColor(doc, COLORS.accent, 'fill');
  doc.rect(0, headerH - 4, pageW, 4, 'F');

  // Logo (quadrado com borda arredondada branca)
  const logoSize = 64;
  const logoX    = margin;
  const logoY    = (headerH - 4 - logoSize) / 2;
  // Textos deslocados para a direita da logo
  const textX = logoX + logoSize + 16;
  if (logoBase64) {
    

    // Fundo branco arredondado atrás da logo
    setColor(doc, COLORS.white, 'fill');
    doc.roundedRect(logoX - 3, logoY - 3, logoSize + 6, logoSize + 6, 4, 4, 'F');

    doc.addImage(logoBase64, 'JPEG', logoX, logoY, logoSize, logoSize);

    

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    setColor(doc, COLORS.white);
    doc.text(invoice.isReceipt ? 'RECIBO' : 'ORCAMENTO', textX, 34);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    setColor(doc, COLORS.accentLight);
    doc.text(`N  ${invoice.number || '-'}`, textX, 52);

    if (user.name) {
      doc.setFontSize(10);
      setColor(doc, [180, 200, 230]);
      doc.text(safe(user.name), textX, 68);
    }
  } else {
    // Sem logo — layout original centrado à esquerda
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    setColor(doc, COLORS.white);
    doc.text(invoice.isReceipt ? 'RECIBO' : 'ORCAMENTO', textX, 34);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    setColor(doc, COLORS.accentLight);
    doc.text(`N  ${invoice.number || '-'}`, margin, 54);

    if (user.name) {
      doc.setFontSize(10);
      setColor(doc, [180, 200, 230]);
      doc.text(safe(user.name), margin, 68);
    }
  }

  // Badge de status (canto superior direito)
  const statusLabel = safe(invoice.status || 'Rascunho').toUpperCase();
  const sc          = statusColor(invoice.status);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const badgeW = doc.getTextWidth(statusLabel) + 18;
  const badgeX = pageW - margin - badgeW;
  const badgeY = 10;

  setColor(doc, sc, 'fill');
  doc.roundedRect(badgeX, badgeY, badgeW, 18, 3, 3, 'F');
  setColor(doc, COLORS.white);
  doc.text(statusLabel, badgeX + badgeW / 2, badgeY + 12, { align: 'center' });

  y = headerH + 16;

  // ══════════════════════════════════════════════════════════════════════════
  // DATAS
  // ══════════════════════════════════════════════════════════════════════════
  const fmt = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '–';

  // Caixinhas de data lado a lado
  const dateBoxW = (pageW - margin * 2 - 10) / 2;

  // Box: Data de emissão
  setColor(doc, COLORS.lightAlt, 'fill');
  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, y, dateBoxW, 36, 3, 3, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.muted);
  doc.text('DATA DE EMISSAO', margin + 10, y + 13);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  setColor(doc, COLORS.dark);
  doc.text(fmt(invoice.dateIssue), margin + 10, y + 28);

  // Box: Vencimento
  if (invoice.dateDue) {
    setColor(doc, COLORS.lightAlt, 'fill');
    setColor(doc, COLORS.border, 'draw');
    doc.roundedRect(margin + dateBoxW + 10, y, dateBoxW, 36, 3, 3, 'FD');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    setColor(doc, COLORS.muted);
    doc.text('VENCIMENTO', margin + dateBoxW + 20, y + 13);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    setColor(doc, COLORS.dark);
    doc.text(fmt(invoice.dateDue), margin + dateBoxW + 20, y + 28);
  }

  y += 52;

  // ══════════════════════════════════════════════════════════════════════════
  // EMITENTE
  // ══════════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, 'Emitente (Prestador de Servico)', y, pageW);

  const provider = invoice.provider || {};
  const col2x    = colMid + 10;
  const colW     = colMid - margin - 20;

  // Linha 1: Nome | CNPJ
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setColor(doc, COLORS.muted);
  doc.text('NOME', margin + 8, y);
  doc.text('CNPJ', col2x, y);
  y += 11;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); setColor(doc, COLORS.dark);
  doc.text(truncate(doc, safe(provider.name) || '-', colW), margin + 8, y);
  doc.text(safe(provider.cnpj) || '-', col2x, y);
  y += 14;

  // Linha 2: Endereço | Telefone
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setColor(doc, COLORS.muted);
  doc.text('ENDERECO', margin + 8, y);
  if (provider.phone) doc.text('TELEFONE', col2x, y);
  y += 11;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); setColor(doc, COLORS.dark);
  doc.text(truncate(doc, safe(provider.address) || '-', colW), margin + 8, y);
  if (provider.phone) doc.text(safe(provider.phone), col2x, y);
  y += 18;

  // ══════════════════════════════════════════════════════════════════════════
  // DESTINATÁRIO
  // ══════════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, 'Destinatario (Cliente)', y, pageW);

  const recipient = invoice.recipient || {};

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setColor(doc, COLORS.muted);
  doc.text('NOME', margin + 8, y);
  doc.text('CPF / CNPJ', col2x, y);
  y += 11;

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); setColor(doc, COLORS.dark);
  doc.text(truncate(doc, safe(recipient.name) || '-', colW), margin + 8, y);
  doc.text(safe(recipient.cpfCnpj) || '-', col2x, y);
  y += 14;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); setColor(doc, COLORS.muted);
  doc.text('ENDERECO', margin + 8, y);
  if (recipient.phone) doc.text('TELEFONE', col2x, y);
  y += 11;

  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); setColor(doc, COLORS.dark);
  doc.text(truncate(doc, safe(recipient.address) || '-', colW), margin + 8, y);
  if (recipient.phone) doc.text(safe(recipient.phone), col2x, y);
  y += 18;

  // ══════════════════════════════════════════════════════════════════════════
  // TABELA DE SERVIÇOS
  // ══════════════════════════════════════════════════════════════════════════
  y = sectionHeader(doc, 'Servicos', y, pageW);

  const tableW   = pageW - margin * 2;
  const rowH     = 20;

  // Cabeçalho da tabela
  setColor(doc, COLORS.primary, 'fill');
  doc.rect(margin, y, tableW, rowH, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.white);

  const cols = {
    num:      margin + 8,
    desc:     margin + 26,
    qtd:      margin + 290,
    unit:     margin + 340,
    subtotal: margin + 415,
  };

  doc.text('#',             cols.num,      y + 13);
  doc.text('DESCRICAO',     cols.desc,     y + 13);
  doc.text('QTD',           cols.qtd,      y + 13);
  doc.text('UNIT. (R$)',    cols.unit,     y + 13);
  doc.text('SUBTOTAL (R$)', cols.subtotal, y + 13);
  y += rowH;

  const services = invoice.services || [];
  services.forEach((s, i) => {
    const isEven = i % 2 === 0;
    setColor(doc, isEven ? COLORS.white : COLORS.lightAlt, 'fill');
    doc.rect(margin, y, tableW, rowH, 'F');

    const subtotal = (s.amount * s.valueUnit).toFixed(2);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    setColor(doc, COLORS.dark);

    doc.text(String(i + 1),                                  cols.num,      y + 13);
    doc.text(truncate(doc, safe(s.description) || '', 240),  cols.desc,     y + 13);
    doc.text(String(s.amount),                               cols.qtd,      y + 13);
    doc.text(Number(s.valueUnit).toFixed(2),                 cols.unit,     y + 13);
    doc.text(subtotal,                                       cols.subtotal, y + 13);

    // Linha divisória entre linhas
    setColor(doc, COLORS.border, 'draw');
    doc.setLineWidth(0.2);
    doc.line(margin, y + rowH, margin + tableW, y + rowH);

    y += rowH;
  });

  // Borda externa da tabela
  setColor(doc, COLORS.border, 'draw');
  doc.setLineWidth(0.5);
  doc.rect(margin, y - services.length * rowH - rowH, tableW, services.length * rowH + rowH);

  y += 16;

  // ══════════════════════════════════════════════════════════════════════════
  // IMPOSTOS
  // ══════════════════════════════════════════════════════════════════════════
  const taxes      = invoice.taxes || {};
  const taxEntries = Object.entries(taxes).filter(([, v]) => Number(v) > 0);

  if (taxEntries.length > 0) {
    y = sectionHeader(doc, 'Impostos', y, pageW);

    taxEntries.forEach(([name, value], i) => {
      if (i % 2 === 0) {
        setColor(doc, COLORS.white, 'fill');
      } else {
        setColor(doc, COLORS.lightAlt, 'fill');
      }
      doc.rect(margin, y - 11, tableW, 18, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      setColor(doc, COLORS.muted);
      doc.text(`${name.toUpperCase()}`, margin + 8, y);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10.5);
      setColor(doc, COLORS.dark);
      doc.text(`R$ ${Number(value).toFixed(2)}`, margin + 120, y);
      y += 18;
    });
    y += 6;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // TOTAL
  // ══════════════════════════════════════════════════════════════════════════

  // Sombra simulada
  setColor(doc, COLORS.border, 'fill');
  doc.roundedRect(margin + 2, y + 2, tableW, 40, 4, 4, 'F');

  // Box principal
  setColor(doc, COLORS.primary, 'fill');
  doc.roundedRect(margin, y, tableW, 40, 4, 4, 'F');

  // Faixa lateral decorativa no total
  setColor(doc, COLORS.accent, 'fill');
  doc.roundedRect(margin, y, 6, 40, 4, 4, 'F');
  doc.rect(margin, y, 6, 40, 'F'); // retifica canto direito da faixa

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setColor(doc, [180, 200, 230]);
  doc.text('VALOR TOTAL', margin + 16, y + 16);

  doc.setFontSize(9);
  setColor(doc, COLORS.accentLight);
  doc.text(`${services.length} servico(s)`, margin + 16, y + 30);

  const totalStr = `R$ ${Number(invoice.totalValue).toFixed(2)}`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  setColor(doc, COLORS.white);
  doc.text(totalStr, pageW - margin - 12, y + 26, { align: 'right' });

  y += 56;

  // ══════════════════════════════════════════════════════════════════════════
  // OBSERVAÇÕES
  // ══════════════════════════════════════════════════════════════════════════
  if (invoice.obs) {
    y = sectionHeader(doc, 'Observacoes', y, pageW);

    setColor(doc, COLORS.lightAlt, 'fill');
    const obsLines = doc.splitTextToSize(safe(invoice.obs), tableW - 16);
    const obsH     = obsLines.length * 13 + 14;
    doc.rect(margin, y - 6, tableW, obsH, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10.5);
    setColor(doc, COLORS.dark);
    doc.text(obsLines, margin + 8, y + 4);
    y += obsH + 6;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // ASSINATURA (somente recibo)
  // ══════════════════════════════════════════════════════════════════════════
  if (invoice.isReceipt) {
    // Garante espaço mínimo antes do rodapé; se não houver, poderia
    // futuramente virar nova página — por ora, reserva área fixa.
    const signatureY = Math.max(y + 40, pageH - 110);

    const lineW = 240;
    const lineX = colMid - lineW / 2;

    setColor(doc, COLORS.dark, 'draw');
    doc.setLineWidth(0.6);
    doc.line(lineX, signatureY, lineX + lineW, signatureY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    setColor(doc, COLORS.muted);
    doc.text('Assinatura do prestador', colMid, signatureY + 14, { align: 'center' });

    y = signatureY + 14;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RODAPÉ
  // ══════════════════════════════════════════════════════════════════════════

  // Faixa decorativa acima do rodapé
  setColor(doc, COLORS.accent, 'fill');
  doc.rect(0, pageH - 40, pageW, 2, 'F');

  setColor(doc, COLORS.light, 'fill');
  doc.rect(0, pageH - 38, pageW, 38, 'F');

  const now = new Date();
  const footerLeft  = safe(user.name || '');
  const footerRight = `Gerado em ${now.toLocaleDateString('pt-BR')} as ${now.toLocaleTimeString('pt-BR')}`;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.primary);
  if (footerLeft) doc.text(footerLeft, margin, pageH - 18);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.muted);
  doc.text(footerRight, pageW - margin, pageH - 18, { align: 'right' });

  // Número de página
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  setColor(doc, COLORS.muted);
  doc.text('Pagina 1 de 1', colMid, pageH - 10, { align: 'center' });

  // ══════════════════════════════════════════════════════════════════════════
  // DOWNLOAD (sem file:///)
  // ══════════════════════════════════════════════════════════════════════════
  const fileName = invoice.isReceipt
    ? `recibo-${invoice.number || 'sem-numero'}.pdf`
    : `orçamento-${invoice.number || 'sem-numero'}.pdf`;
  const blob     = doc.output('blob');
  const url      = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href     = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}